/**
 * Sample Size generator (6.DPSR.1.1).
 *
 * The sample size is simply the number of data points collected. The data set
 * is generated first and the answer is its length, so the displayed values and
 * the graded count can never disagree.
 *
 * Families:
 *   F1: count the values in a listed data set          (number)
 *   F2: count the responses in a described survey set   (number)
 *   F3: count the measurements in a recorded log        (number)
 */

import { rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "sample-size" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3";

interface Cfg {
  readonly families: readonly Family[];
  readonly minCount: number;
  readonly maxCount: number;
  /** Largest value that can appear in a data set. */
  readonly valueMax: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], minCount: 5, maxCount: 8, valueMax: 20 },
  standard: { families: ["F1", "F2", "F3"], minCount: 6, maxCount: 11, valueMax: 40 },
  hard: { families: ["F1", "F2", "F3"], minCount: 8, maxCount: 15, valueMax: 80 },
  challenge: { families: ["F2", "F3"], minCount: 10, maxCount: 15, valueMax: 100 },
};

/** A described data-collection context: a noun and a wording. */
interface Context {
  readonly listed: (values: string) => string;
  readonly described: (values: string) => string;
  readonly logged: (values: string) => string;
}

const CONTEXTS: readonly Context[] = [
  {
    listed: (v) => `A data set has the values ${v}.`,
    described: (v) => `Students recorded their scores: ${v}.`,
    logged: (v) => `A sensor logged these readings: ${v}.`,
  },
  {
    listed: (v) => `A survey collected the responses ${v}.`,
    described: (v) => `Shoppers reported these wait times (minutes): ${v}.`,
    logged: (v) => `A weather station recorded these temperatures: ${v}.`,
  },
  {
    listed: (v) => `The following measurements were taken: ${v}.`,
    described: (v) => `Runners posted these lap times (seconds): ${v}.`,
    logged: (v) => `Daily rainfall (mm) was recorded as ${v}.`,
  },
];

export class SampleSizeGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);
      const context = rng.pick(CONTEXTS);

      const count = rng.int(cfg.minCount, cfg.maxCount);
      const values: number[] = [];
      for (let i = 0; i < count; i++) values.push(rng.int(1, cfg.valueMax));
      const valueList = values.join(", ");

      const sentence =
        family === "F1"
          ? context.listed(valueList)
          : family === "F2"
            ? context.described(valueList)
            : context.logged(valueList);

      const answer = rat(count);

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: {
          text: `${sentence} What is the sample size?`,
        },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps: [
          {
            description:
              "The sample size is the number of data points collected. Count each value in the set.",
            latex: answer.toLatex(),
          },
        ],
        hints: [
          { level: 1, text: "The sample size counts how many values were collected." },
          { level: 2, text: "Count each number in the list once." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const sampleSize = new SampleSizeGenerator();
